import React, { useState, useRef, useEffect, useCallback } from 'react';
import * as styles from './RateModalForm.module.scss';
import { StepGroup, Step } from './Step';
import RateModalEmail from './RateModalEmail';
import Form from '@components/Form';
import { Dot } from '@components/Timeline';
import Badge from '@components/Badge';
import Button from '@input/Button';
import ToggleButton from '@input/ToggleButton';
import useConfig from '@hooks/useConfig';
import { SAVE_LOAN_DATA_ENDPOINT } from '@constants';
import { columnKeys } from '@enums';
import {
  isRefinance,
  formatCurrency,
  isValidEmail,
  isValidPhone,
  formatParagraphs
} from '@helpers';
import classNames from 'classnames';

export default function RateModalForm({ product, loanDetails, className }) {
  //////////////////////////
  // Apply now flow
  //////////////////////////

  const [action, setAction] = useState(null);
  const isApply = action === 'apply';
  const isConsultation = action === 'consultation';
  const hasSelectedAction = isApply || isConsultation;
  const [emailState, setEmailState] = useState({
    value: '',
    lastValidEmail: '',
    isValid: false,
    canShowError: false
  });

  const productText = isRefinance(loanDetails.loanPurpose)
    ? 'refinance'
    : 'home purchase';

  const applyButtonText = (
    <>
      <span className={styles.longText}>Apply now online</span>
      <span className={styles.shortText}>Apply now</span>
    </>
  );

  const consultationButtonText = (
    <>
      <span className={styles.longText}>Request a consultation</span>
      <span className={styles.shortText}>Request a call</span>
    </>
  );

  const consultationForm = useRef();
  const focusedConsultationInput = useRef(null);

  useEffect(() => {
    const form = consultationForm.current;

    if (isConsultation && form) {
      const prevInput = focusedConsultationInput.current;

      if (prevInput && !prevInput.value) {
        prevInput.focus();
      } else {
        const fields = form.querySelectorAll('input');
        const input = Array.from(fields).find(el => {
          if (el.type === 'tel' && !isValidPhone(el.value)) return true;
          if (el.type === 'email' && !isValidEmail(el.value)) return true;
          return !el.value;
        });

        if (input) input.focus();
      }
    }
  }, [isConsultation]);

  const {
    selectActionLabel = 'How would you like to proceed?',
    emailFieldLabel = 'What is your email address?',
    emailFieldDescription,
    loginFieldLabel = 'Do you have an existing personal online banking account?',
    loginFieldDescription,
    consultationFieldLabel = 'Our expert loan officers will help guide you through your application',
    consultationFieldDescription,
    blendOnlineBankingSSOLink,
    blendSignUpLink
  } = useConfig();

  const handleApplyClick = useCallback(
    async e => {
      if (!emailState.lastValidEmail || !product || !loanDetails) return;

      const node =
        e.currentTarget?.tagName === 'A'
          ? e.currentTarget
          : e.target.closest('a');
      if (!node || !node.href) return;
      e.preventDefault();

      const redirectUrl = node.href ?? window.location.href;

      // Prevents speedbump from showing without having to
      // add the staging Blend site to the public whitelist
      if (redirectUrl.includes('blendlabs')) {
        e.nativeEvent.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
      }

      const args = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Encoding': 'gzip, deflate',
          Accept: '*/*'
        },
        body: JSON.stringify({
          email: emailState.lastValidEmail,
          ...product,
          ...loanDetails
        })
      };

      try {
        await fetch(SAVE_LOAN_DATA_ENDPOINT, args);
        window.location.href = redirectUrl;
      } catch (error) {
        console.error(error);
        window.location.href = redirectUrl;
      }
    },
    [emailState.lastValidEmail, product, loanDetails]
  );

  return (
    <div
      className={classNames(styles.wrapper, className, {
        [styles.hasHighlights]: product && loanDetails
      })}
    >
      <div className={styles.content}>
        <div className={styles.contentInner}>
          <h3>Let's get your {productText} started</h3>

          <Highlights product={product} loanDetails={loanDetails} />

          <div className={styles.stepWrapper}>
            <StepGroup isVisible>
              <Step
                number={1}
                label={selectActionLabel}
                isComplete={hasSelectedAction}
                isVisible
              >
                <div className={styles.buttons}>
                  <ToggleButton
                    text={applyButtonText}
                    isActive={isApply}
                    onClick={() => setAction('apply')}
                  />
                  <ToggleButton
                    text={consultationButtonText}
                    isActive={isConsultation}
                    onClick={() => setAction('consultation')}
                  />
                </div>
              </Step>
            </StepGroup>

            <div
              className={classNames(styles.stepGroupWrapper, {
                [styles.visible]: isApply || isConsultation
              })}
            >
              <Dot
                number={2}
                className={styles.dot}
                isMainTimeline={false}
                showOnMobile
                color={
                  (isApply && emailState.lastValidEmail) || isConsultation
                    ? 'teal'
                    : 'gray'
                }
              />

              <StepGroup isVisible={isApply}>
                <Step
                  className={styles.emailStep}
                  label={emailFieldLabel}
                  labelId="modal-form-apply-email"
                  isVisible={isApply}
                  isComplete={!!emailState.lastValidEmail}
                >
                  {formatParagraphs(emailFieldDescription, false)}
                  <RateModalEmail
                    emailState={emailState}
                    setEmailState={setEmailState}
                    isVisible={isApply}
                  />
                </Step>

                <Step
                  number={3}
                  label={loginFieldLabel}
                  className={styles.applyStep}
                  isVisible={isApply && emailState.lastValidEmail}
                  canShow={!!emailState.lastValidEmail}
                  showLine={false}
                >
                  <div className={styles.buttons}>
                    <Button
                      href={blendOnlineBankingSSOLink}
                      onClickCapture={handleApplyClick}
                    >
                      Yes, log in to apply
                    </Button>

                    <Button
                      href={blendSignUpLink}
                      onClickCapture={handleApplyClick}
                    >
                      No, continue to application
                    </Button>
                  </div>

                  {formatParagraphs(loginFieldDescription, false)}
                </Step>
              </StepGroup>

              <StepGroup isVisible={isConsultation}>
                <Step
                  label={consultationFieldLabel}
                  isVisible={isConsultation}
                  showLine={false}
                >
                  {formatParagraphs(consultationFieldDescription, false)}
                  <div
                    ref={consultationForm}
                    className={styles.form}
                    onFocus={e => {
                      if (e.target.tagName === 'INPUT') {
                        focusedConsultationInput.current = e.target;
                      }
                    }}
                  >
                    <Form product={product} loanDetails={loanDetails} />
                  </div>
                </Step>
              </StepGroup>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// const Highlights = React.memo(function Highlights({
const Highlights = React.memo(function Highlights({ product, loanDetails }) {
  const config = useConfig();
  const wrapper = useRef();

  if (!product || !loanDetails) {
    return null;
  }

  const productLabel = product.productLabel
    .split(' ')
    .map(word => {
      return word.toUpperCase().replace(/(\/\d+)M$/, '$1m');
    })
    .join(' ');

  const cashOutAmount = parseFloat(loanDetails.cashOutAmount);

  // Display product
  const productBadges = [];
  const otherBadges = [];

  product.badges?.forEach(key => {
    const badge = config.badges[key];

    if (!badge || !badge.showInRateModal || !badge.rateModalBadgeText) {
      return;
    }

    const badgeComponent = (
      <Badge
        key={key}
        className={styles.badge}
        badgeName={badge.badgeName}
        badgeStyle={badge.badgeStyle}
        tooltip={badge.tooltipText}
        tooltipContainer={wrapper}
        minTooltipLeftOffset={0}
        maxTooltipRightOffset={0}
      >
        {badge.rateModalBadgeText}
      </Badge>
    );

    if (badge.badgeDisplayColumn === columnKeys.PRODUCT) {
      productBadges.push(badgeComponent);
    } else {
      otherBadges.push(badgeComponent);
    }
  });

  return (
    <div ref={wrapper} className={styles.highlightsWrapper}>
      {productBadges}
      <Highlight uppercase={false}>{productLabel}</Highlight>
      <Highlight>{formatCurrency(loanDetails.loanAmount)} loan</Highlight>
      {isRefinance(loanDetails.loanPurpose) &&
        !!cashOutAmount &&
        !isNaN(cashOutAmount) && (
          <Highlight>{formatCurrency(cashOutAmount)} cash out</Highlight>
        )}
      {otherBadges}
      {/* {product.isNCC && <Highlight>No Closing Costs</Highlight>} */}
    </div>
  );
});

function Highlight({ children, uppercase = true }) {
  const style = {
    textTransform: !uppercase ? null : 'uppercase'
  };

  return (
    <div style={style} className={styles.highlight}>
      {children}
    </div>
  );
}
